import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import SaveIcon from '@mui/icons-material/Save';
import SyncIcon from '@mui/icons-material/Sync';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { makeAuthenticatedApiCall } from '../apiHelper';
import { useAuth } from '../components/AuthContext';
import { useRFAFees } from '../hooks/useApiQueries';
import { getAllFranchiseIds, getFranchise } from '../utils';

interface FranchiseFee {
  franchise_id: string;
  cg_fee: number | null;
  da_fee: number | null;
}

const RFAAdminPage = () => {
  const { currentUser } = useAuth();
  const userRole = currentUser?.data?.data?.role;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading } = useRFAFees();

  // Récupérer tous les IDs de franchises depuis la fonction utilitaire centralisée
  const allFranchises = getAllFranchiseIds();

  const [franchiseFees, setFranchiseFees] = useState<Record<string, FranchiseFee>>({});
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [savingStates, setSavingStates] = useState<Record<string, boolean>>({});
  const [isSyncing, setIsSyncing] = useState(false);

  // Check user is god
  useEffect(() => {
    if (userRole && userRole !== 'god') {
      navigate('/');
    }
  }, [userRole, navigate]);

  // Load data from server
  useEffect(() => {
    if (data?.data) {
      const feesMap: Record<string, FranchiseFee> = {};

      // Initialize all franchises with null values
      allFranchises.forEach((franchiseId) => {
        feesMap[franchiseId] = {
          franchise_id: franchiseId,
          cg_fee: null,
          da_fee: null,
        };
      });

      // Override with actual data from server
      data.data.forEach((fee: FranchiseFee) => {
        feesMap[fee.franchise_id] = fee;
      });

      setFranchiseFees(feesMap);
    }
  }, [data]);

  const handleCgFeeChange = (franchiseId: string, value: string) => {
    setFranchiseFees((prev) => ({
      ...prev,
      [franchiseId]: {
        ...prev[franchiseId],
        cg_fee: value === '' ? null : parseFloat(value),
      },
    }));
  };

  const handleDaFeeChange = (franchiseId: string, value: string) => {
    setFranchiseFees((prev) => ({
      ...prev,
      [franchiseId]: {
        ...prev[franchiseId],
        da_fee: value === '' ? null : parseFloat(value),
      },
    }));
  };

  const handleSave = async (franchiseId: string) => {
    try {
      setSavingStates((prev) => ({ ...prev, [franchiseId]: true }));
      setSuccessMessage('');
      setErrorMessage('');

      const fee = franchiseFees[franchiseId];

      const payload = {
        franchise_id: franchiseId,
        cg_fee: fee.cg_fee,
        da_fee: fee.da_fee,
      };

      await makeAuthenticatedApiCall('post', '/api/rfa/fees', payload);

      // Invalidate cache
      queryClient.invalidateQueries({ queryKey: ['rfaFees'] });

      setSuccessMessage(`Configuration sauvegardée pour ${getFranchise(franchiseId)}`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error: any) {
      console.error('Error saving fee:', error);
      setErrorMessage(error.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingStates((prev) => ({ ...prev, [franchiseId]: false }));
    }
  };

  const handleSyncMappings = async () => {
    try {
      setIsSyncing(true);
      setSuccessMessage('');
      setErrorMessage('');

      await makeAuthenticatedApiCall('post', '/api/rfa/mappings/sync');

      // Les tarifs affichés dépendent des mappings : on rafraîchit après sync
      queryClient.invalidateQueries({ queryKey: ['rfaFees'] });

      setSuccessMessage('Mappings franchise / client synchronisés');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error: any) {
      console.error('Error syncing mappings:', error);
      setErrorMessage(error.response?.data?.message || 'Erreur lors de la synchronisation des mappings');
    } finally {
      setIsSyncing(false);
    }
  };

  if (userRole !== 'god') {
    return null;
  }

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Tarifs
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configurez les tarifs personnalisés pour chaque franchise. Les valeurs vides utilisent les tarifs par défaut
            (CG: 10€, DA: 10€).
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={isSyncing ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
          onClick={handleSyncMappings}
          disabled={isSyncing}
          sx={{ flexShrink: 0 }}
        >
          {isSyncing ? 'Synchronisation...' : 'Sync mappings'}
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Les mappings franchise / client sont synchronisés automatiquement toutes les heures. Utilisez « Sync mappings »
        pour forcer une mise à jour immédiate.
      </Typography>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Franchise</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Tarifs CG (€)</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Tarifs DA (€)</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Actions</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allFranchises.map((franchiseId) => {
                  const fee = franchiseFees[franchiseId] || {
                    franchise_id: franchiseId,
                    cg_fee: null,
                    da_fee: null,
                  };
                  const isSaving = savingStates[franchiseId] || false;

                  return (
                    <TableRow key={franchiseId}>
                      <TableCell>{getFranchise(franchiseId)}</TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          className="inputElement"
                          value={fee.cg_fee ?? ''}
                          onChange={(e) => handleCgFeeChange(franchiseId, e.target.value)}
                          placeholder="10.00"
                          inputProps={{
                            step: '0.01',
                            min: '0',
                          }}
                          size="small"
                          sx={{ width: '120px' }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          className="inputElement"
                          value={fee.da_fee ?? ''}
                          onChange={(e) => handleDaFeeChange(franchiseId, e.target.value)}
                          placeholder="10.00"
                          inputProps={{
                            step: '0.01',
                            min: '0',
                          }}
                          size="small"
                          sx={{ width: '120px' }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          className="dealActionButton"
                          variant="contained"
                          startIcon={<SaveIcon />}
                          onClick={() => handleSave(franchiseId)}
                          disabled={isSaving}
                          size="small"
                        >
                          {isSaving ? <CircularProgress size={20} /> : 'Sauvegarder'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Box sx={{ mt: 3 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Note:</strong>
          <br />
          • CG: Commission pour les changements de titulaire (Pipeline F). Défaut: 10€
          <br />
          • DA: Prix des déclarations d'achat (Pipeline DA). Défaut: 10€
          <br />• Biwiz (franchise 59) a déjà été configuré avec 12€ pour CG (ancien comportement codé en dur)
        </Typography>
      </Box>
    </Container>
  );
};

export default RFAAdminPage;
